import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  Tooltip,
} from "@salt-ds/core";
import { useComponentCssInjection } from "@salt-ds/styles";
import { useWindow } from "@salt-ds/window";
import { CommitHandler } from "@vuu-ui/vuu-utils";
import cx from "clsx";
import { ForwardedRef, forwardRef, SyntheticEvent, useCallback } from "react";
import { type ToggleFilterHookProps, useToggleFilter } from "./useToggleFilter";

export type ToggleFilterValue = string | number | boolean;

type Value = ToggleButtonGroupProps["value"];

const toToggleButtonValue = (
  value: ToggleFilterValue | Value | undefined,
): Value | undefined => (typeof value === "boolean" ? `${value}` : value);

const getValues = (
  defaultValueProp: ToggleFilterValue | Value | undefined,
  valueProp: ToggleFilterValue | Value | undefined,
) => {
  const defaultValue = toToggleButtonValue(defaultValueProp);
  const value = toToggleButtonValue(valueProp);
  if (defaultValue === undefined && value === undefined) {
    return ["all"];
  } else if (defaultValue !== undefined && value !== undefined) {
    throw Error(
      "[ToggleFilter] only one of defaultValue/value must be provided",
    );
  } else if (defaultValue === "") {
    return ["all"];
  } else if (value === "") {
    return [undefined, "all"];
  } else {
    return [defaultValue, value];
  }
};

import toggleFilterCss from "./ToggleFilter.css";
/**
 * ToggleFilter is designed to work with a FilterProvider and
 * DataSourceProvider. With a DataSourceProvider, values will
 * be validated against available values from datasource.
 * With both FilterProvider and a DataSourceProvider, changes to
 * the currentFilter will also trigger re-evaluation of available
 * values from dataSource.
 * With neither of these Providers available, the ToggleFilter is
 * behaving like a regular ToggleButtonGroup.
 * Validation of values uses the typeahead service, which only supports
 * string columns. It can be disabled with disableTypeahead and is always
 * disabled for a non-string (e.g. boolean or numeric) column.
 */
export interface ToggleFilterProps
  extends
    Omit<ToggleButtonGroupProps, "defaultValue" | "value">,
    Omit<ToggleFilterHookProps, "values"> {
  defaultValue?: ToggleFilterValue | Value;
  /**
   * Optional labels for the toggle buttons. If provided, must contain
   * the same number of items as values. Defaults to values.
   */
  labels?: string[];
  onCommit: CommitHandler<HTMLElement>;
  value?: ToggleFilterValue | Value;
  /**
   * Values for the toggle buttons. Boolean values are converted to
   * strings internally, the original value is passed to onCommit.
   */
  values: ToggleFilterValue[];
}

const classBase = "vuuToggleFilter";

const NO_MATCHING_DATA = "No matching data";

export const ToggleFilter = forwardRef(function ToggleFilter(
  {
    className,
    column,
    defaultValue: defaultValueProp,
    disableTypeahead,
    labels,
    onCommit,
    table,
    value: valueProp,
    values,
    ...ToggleButtonGroupProps
  }: ToggleFilterProps,
  forwardedRef: ForwardedRef<HTMLDivElement>,
) {
  const targetWindow = useWindow();
  useComponentCssInjection({
    testId: "vuu-toggle-filter",
    css: toggleFilterCss,
    window: targetWindow,
  });

  if (labels !== undefined && labels.length !== values.length) {
    throw Error(
      "[ToggleFilter] labels must have the same number of items as values",
    );
  }

  const [defaultValue, value] = getValues(defaultValueProp, valueProp);

  const stringValues = values.map(String);

  const { onlyAvailableValue, unavailableValues } = useToggleFilter({
    column,
    disableTypeahead,
    table,
    values: stringValues,
  });

  const handleChange = useCallback(
    (e: SyntheticEvent<HTMLButtonElement>) => {
      const value = e.currentTarget.value;
      if (value === "all") {
        onCommit(e, "");
      } else {
        const toggleValue = values.find((v) => `${v}` === value);
        onCommit(e, typeof toggleValue === "boolean" ? toggleValue : value);
      }
    },
    [onCommit, values],
  );

  return (
    <ToggleButtonGroup
      {...ToggleButtonGroupProps}
      className={cx(classBase, className)}
      defaultValue={defaultValue}
      onChange={handleChange}
      ref={forwardedRef}
      value={value}
    >
      <ToggleButton key="all" value="all">
        All
      </ToggleButton>
      {stringValues.map((toggleValue, i) => {
        const unavailable = unavailableValues.includes(toggleValue);
        return (
          <Tooltip
            content={NO_MATCHING_DATA}
            disabled={!unavailable}
            key={toggleValue}
          >
            <ToggleButton
              className={cx({
                [`${classBase}-onlyAvailableValue`]:
                  onlyAvailableValue === toggleValue,
                [`${classBase}-unavailableValue`]: unavailable,
              })}
              value={toggleValue}
            >
              {labels?.[i] ?? toggleValue}
            </ToggleButton>
          </Tooltip>
        );
      })}
    </ToggleButtonGroup>
  );
});
